import { Button, FormControl, Input } from '@farol-ds/react'
import { FormEvent, useCallback, useState } from 'react'

import type { SignupModalHeaderCopy } from '../../experiments/signup-modal/highlight-copy'
import { ERROR_MESSAGES } from '../../utils/errorMessages'
import { parseErrorMessage } from '../../utils/parseErrorMessage'
import { LoginCard } from '../login-card'
import styles from './password-sign-in.module.scss'

interface PasswordSignInProps {
  email: string
  isLoading?: boolean
  error?: string
  onSubmit: (password: string) => Promise<void>
  onBackToEmail: () => void
  onForgotPassword: () => void
  onCloseClick?: () => void
  signupModalHeader?: SignupModalHeaderCopy
}

export function PasswordSignIn({
  email,
  isLoading,
  error,
  onSubmit,
  onBackToEmail,
  onForgotPassword,
  onCloseClick,
  signupModalHeader,
}: PasswordSignInProps) {
  const [passwordValue, setPasswordValue] = useState('')
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [passwordError, setPasswordError] = useState(false)

  const parsedError = error ? parseErrorMessage(error) : null

  const header = {
    title: signupModalHeader?.title ?? 'Entre ou crie sua conta',
    ...(signupModalHeader?.subtitle ? { subtitle: { content: signupModalHeader.subtitle } } : {}),
    chip: {
      text: email,
      onClose: onBackToEmail,
    },
    ...(parsedError && {
      errorMessage: {
        title: parsedError.title,
        description: parsedError.description,
      },
    }),
  }

  const handlePasswordBlur = useCallback(() => {
    setPasswordTouched(true)
    setPasswordError(!passwordValue.trim())
  }, [passwordValue])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setPasswordTouched(true)
    const empty = !passwordValue.trim()
    setPasswordError(empty)
    if (empty) return
    await onSubmit(passwordValue)
  }

  return (
    <LoginCard header={header} onCloseClick={onCloseClick}>
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        <FormControl error={passwordTouched && passwordError}>
          <FormControl.Label>Senha</FormControl.Label>
          <Input
            data-testid="password-input"
            type="password"
            name="password"
            value={passwordValue}
            onChange={(e) => setPasswordValue(e.target.value)}
            onBlur={handlePasswordBlur}
            disabled={isLoading}
          />
          {passwordTouched && passwordError && (
            <FormControl.ErrorMessage>
              {ERROR_MESSAGES.login.password.validation.empty}
            </FormControl.ErrorMessage>
          )}
        </FormControl>

        <Button
          data-testid="password-submit-button"
          type="submit"
          kind="primary"
          loading={isLoading}
          className={styles.submitButton}
        >
          Entrar
        </Button>

        <div className={styles.forgotPasswordContainer}>
          <Button kind="plain" onClick={onForgotPassword}>
            Esqueceu a senha?
          </Button>
        </div>
      </form>
    </LoginCard>
  )
}
